import React, { useState, useMemo, useEffect } from 'react';
import { useData } from '../../context/DataContext';
import { Restaurant } from '../../types';
import { formatDateReadable } from '../../utils/dateUtils';
import { 
  StarIcon, 
  TrashIcon, 
  PencilIcon, 
  XIcon, 
  ClockIcon,
  ShieldCheckIcon,
  UserIcon,
  PhoneIcon,
  ClipboardListIcon,
  ShieldExclamationIcon
} from '../../components/icons';

const SuperAdminRestaurants: React.FC = () => {
  const { 
    restaurants, 
    validateRestaurant, 
    suspendRestaurant, 
    deleteRestaurant,
    toggleRestaurantFeatured,
    grantFreeSubscription,
    calculateRestaurantRevenue,
    allPayments,
    validatePayment,
    rejectPayment
  } = useData();
  
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'suspended'>('all');
  const [revenues, setRevenues] = useState<Record<string, { today: number, week: number, month: number, currentCommission: number }>>({});
  const [viewingPaymentImage, setViewingPaymentImage] = useState<string | null>(null);

  // Sync selected restaurant when global list updates
  useEffect(() => {
    if (selectedRestaurant) {
      const updated = restaurants.find(r => r.id === selectedRestaurant.id);
      if (updated) setSelectedRestaurant(updated);
    }
  }, [restaurants]);

  const filteredRestaurants = useMemo(() => {
    return restaurants.filter(r => {
      const matchesSearch = r.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            r.wilaya?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filter === 'all' || r.status === filter;
      return matchesSearch && matchesFilter;
    });
  }, [restaurants, searchTerm, filter]);

  const handleOpenDetail = async (resto: Restaurant) => {
    setSelectedRestaurant(resto);
    
    // Fetch revenues
    const now = new Date();
    const todayStart = new Date(now.setHours(0,0,0,0));
    const weekStart = new Date(new Date().setDate(now.getDate() - 7));
    const monthStart = new Date(new Date().setMonth(now.getMonth() - 1));
    
    const [today, week, month, sinceLastPayment] = await Promise.all([
      calculateRestaurantRevenue(resto.id, todayStart, new Date()),
      calculateRestaurantRevenue(resto.id, weekStart, new Date()),
      calculateRestaurantRevenue(resto.id, monthStart, new Date()),
      resto.commissionStartedAt ? calculateRestaurantRevenue(resto.id, 
        resto.commissionStartedAt.toDate ? resto.commissionStartedAt.toDate() : new Date(resto.commissionStartedAt), 
        new Date()) : Promise.resolve(0)
    ]);
    
    setRevenues(prev => ({
      ...prev,
      [resto.id]: { today, week, month, currentCommission: Math.round(sinceLastPayment * 0.05) }
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 pb-24">
      <h1 className="text-2xl font-black text-gray-900 mb-6">Gestion des Restaurants</h1>
      
      {/* Filters & Search */}
      <div className="flex flex-col gap-4 mb-6">
        <input 
          type="text" 
          placeholder="Rechercher un restaurant..." 
          className="w-full p-4 rounded-2xl bg-white border border-gray-100 shadow-sm focus:ring-2 focus:ring-red-500 outline-none transition-all"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {(['all', 'pending', 'approved', 'suspended'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-widest whitespace-nowrap transition-all ${
                filter === f ? 'bg-red-600 text-white shadow-lg shadow-red-200' : 'bg-white text-gray-400 border border-gray-100'
              }`}
            >
              {f === 'all' ? 'Tous' : f === 'pending' ? 'En attente' : f === 'approved' ? 'Actifs' : 'Suspendus'}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="space-y-4">
        {filteredRestaurants.map((resto) => (
          <div 
            key={resto.id} 
            className="bg-white p-4 rounded-3xl shadow-sm border border-gray-50 flex flex-col md:flex-row gap-4"
          >
            <div className="flex items-center gap-4 flex-1">
               <img src={resto.image} alt="" className="w-16 h-16 rounded-2xl object-cover bg-gray-100 shadow-inner" />
               <div className="flex-1">
                 <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-black text-gray-900 line-clamp-1">{resto.name}</h3>
                    {resto.subscriptionType === 'trial' ? (
                      <span className="bg-blue-50 text-blue-600 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase">Essai</span>
                    ) : (
                      <span className="bg-green-50 text-green-600 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase">Abonné</span>
                    )}
                 </div>
                 <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 font-medium">{resto.wilaya}</span>
                    <a href={`tel:${resto.phone}`} className="flex items-center gap-1 text-xs text-blue-600 font-black hover:underline">
                      <PhoneIcon className="w-3 h-3" />
                      {resto.phone}
                    </a>
                  </div>
                 {(resto.ownerName || (resto as any).owner_name) && (
                   <div className="flex items-center text-[10px] text-red-600 mt-1 font-bold">
                     <UserIcon className="w-3 h-3 mr-1" /> {resto.ownerName || (resto as any).owner_name}
                   </div>
                 )}
                 <div className="flex items-center gap-1 mt-1">
                   <StarIcon className="w-3 h-3 text-yellow-500" />
                   <span className="text-[10px] font-bold text-gray-700">{resto.rating} ({resto.ratingCount})</span>
                 </div>
               </div>
            </div>

            <div className="flex gap-2 items-center">
               {resto.status === 'pending' && (
                 <button 
                  onClick={() => validateRestaurant(resto.id, 'approved')}
                  className="bg-green-600 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-green-700 transition-colors shadow-lg shadow-green-100"
                 >
                   Valider
                 </button>
               )}
               <button 
                onClick={() => handleOpenDetail(resto)}
                className="bg-gray-100 text-gray-600 p-2 rounded-xl hover:bg-gray-200 transition-colors"
               >
                 <PencilIcon className="w-5 h-5" />
               </button>
               <button 
                onClick={() => { if(confirm('Suspendre?')) suspendRestaurant(resto.id) }}
                className="bg-orange-50 text-orange-600 p-2 rounded-xl hover:bg-orange-100 transition-colors"
               >
                 <ShieldExclamationIcon className="w-5 h-5" />
               </button>
               <button 
                onClick={() => { if(confirm('Supprimer?')) deleteRestaurant(resto.id) }}
                className="bg-red-50 text-red-600 p-2 rounded-xl hover:bg-red-100 transition-colors"
               >
                 <TrashIcon className="w-5 h-5" />
               </button>
            </div>
          </div>
        ))}
      </div>

      {/* Detail Modal */}
      {selectedRestaurant && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-t-[40px] md:rounded-[40px] max-h-[90vh] overflow-y-auto p-8 relative animate-slide-up">
            <button 
              onClick={() => setSelectedRestaurant(null)}
              className="absolute top-6 right-6 bg-gray-100 p-2 rounded-full text-gray-500"
            >
              <XIcon className="w-6 h-6" />
            </button>

            <div className="flex flex-col md:flex-row gap-6 mb-8">
               <img src={selectedRestaurant.image} alt="" className="w-32 h-32 rounded-[2.5rem] object-cover shadow-2xl" />
               <div>
                  <h2 className="text-2xl font-black text-gray-900 mb-1">{selectedRestaurant.name}</h2>
                  <p className="text-gray-500 font-medium mb-1 text-sm">{selectedRestaurant.address}, {selectedRestaurant.wilaya}</p>
                  <div className="flex flex-wrap gap-3 mb-4">
                     {(selectedRestaurant.ownerName || (selectedRestaurant as any).owner_name) && (
                       <div className="flex items-center text-xs text-gray-700 bg-gray-50 px-3 py-1.5 rounded-2xl border border-gray-100">
                         <UserIcon className="w-3.5 h-3.5 mr-2 text-red-500" />
                         <span className="font-black mr-1 text-gray-900">Propriétaire:</span> {selectedRestaurant.ownerName || (selectedRestaurant as any).owner_name}
                       </div>
                     )}
                    <a 
                      href={`tel:${selectedRestaurant.phone}`}
                      className="flex items-center text-xs text-gray-700 bg-gray-50 px-3 py-1.5 rounded-2xl border border-gray-100 hover:bg-gray-100 transition-colors"
                    >
                      <PhoneIcon className="w-3.5 h-3.5 mr-2 text-red-500" />
                      <span className="font-black mr-1 text-gray-900">Tél:</span> {selectedRestaurant.phone}
                    </a>
                  </div>
                  <div className="flex gap-2">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${selectedRestaurant.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {selectedRestaurant.status}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-[10px] font-black uppercase">
                      {selectedRestaurant.subscriptionType}
                    </span>
                  </div>
               </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
               {/* Stats Card */}
               <div className="bg-gray-50 p-6 rounded-3xl">
                  <h3 className="font-black text-gray-900 mb-4 flex items-center gap-2">
                    <StarIcon className="w-5 h-5 text-yellow-500" />
                    Performances
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-500 text-sm">Aujourd'hui</span>
                      <span className="font-bold text-gray-900 text-sm">{revenues[selectedRestaurant.id]?.today || 0} DA</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 text-sm">Cette semaine</span>
                      <span className="font-bold text-gray-900 text-sm">{revenues[selectedRestaurant.id]?.week || 0} DA</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 text-sm">Ce mois</span>
                      <span className="font-bold text-gray-900 text-sm">{revenues[selectedRestaurant.id]?.month || 0} DA</span>
                    </div>
                  </div>
               </div>

               {/* Subscription/Visibility Card */}
               <div className="bg-gray-50 p-6 rounded-3xl text-left">
                  <h3 className="font-black text-gray-900 mb-4 flex items-center gap-2">
                    <ClockIcon className="w-5 h-5 text-red-500" />
                    Abonnement & Visibilité
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Expiration Plan</p>
                      <p className="font-bold text-gray-900 text-sm">
                        {formatDateReadable(selectedRestaurant.subscriptionExpiry)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Statut Vedette</p>
                      <p className={`font-bold text-sm uppercase ${selectedRestaurant.featuredStatus === 'active' ? 'text-green-600' : 'text-gray-900'}`}>
                        {selectedRestaurant.featuredStatus === 'active' ? 'Actif' : 'Standard'}
                      </p>
                    </div>
                  </div>
               </div>
            </div>

            {/* NEW: Financial Summary Section (for commission restaurants) */}
            {selectedRestaurant.subscriptionType === 'commission' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-red-50 p-4 rounded-3xl border border-red-100">
                  <p className="text-[9px] font-black text-red-400 uppercase tracking-widest mb-1">Dette Reportée</p>
                  <p className="text-xl font-black text-red-600">{Number(selectedRestaurant.commissionDebt || 0).toLocaleString()} DA</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-3xl border border-blue-100">
                  <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1">Encours (Frais 5%)</p>
                  <p className="text-xl font-black text-blue-600">{(revenues[selectedRestaurant.id]?.currentCommission || 0).toLocaleString()} DA</p>
                </div>
                <div className="bg-gray-900 p-4 rounded-3xl shadow-xl">
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Total à Percevoir</p>
                  <p className="text-xl font-black text-white">
                    {(Number(selectedRestaurant.commissionDebt || 0) + (revenues[selectedRestaurant.id]?.currentCommission || 0)).toLocaleString()} DA
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-4 mb-8">
              <button 
                onClick={() => {
                  const becomesFeatured = selectedRestaurant.featuredStatus !== 'active';
                  if (becomesFeatured && !confirm('Offrir 7 jours de visibilité Vedette à ce restaurant ?')) return;
                  toggleRestaurantFeatured(selectedRestaurant.id, becomesFeatured);
                }}
                className={`flex-1 py-4 rounded-2xl font-black uppercase text-sm shadow-lg transition-all active:scale-95 ${
                  selectedRestaurant.featuredStatus === 'active' 
                    ? 'bg-orange-50 text-orange-600 border border-orange-100' 
                    : 'bg-yellow-500 text-white shadow-yellow-100'
                }`}
              >
                {selectedRestaurant.featuredStatus === 'active' ? 'Retirer des Vedettes' : 'Offrir 7 Jours Vedette ⭐'}
              </button>
            </div>

            {/* Subscription Rewards Section */}
            <div className="mb-8 p-6 bg-red-50 rounded-[2.5rem] border border-red-100">
               <h3 className="font-black text-red-900 mb-4 flex items-center gap-2">
                 <ShieldCheckIcon className="w-5 h-5 text-red-600" />
                 Récompenses & Abonnements
               </h3>
               <div className={`grid ${(!selectedRestaurant.subscriptionType || selectedRestaurant.subscriptionType === 'none' || selectedRestaurant.subscriptionType === 'trial') ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
                  {(selectedRestaurant.subscriptionType !== 'commission') && (
                    <button 
                      onClick={async () => {
                        if (confirm('Offrir 30 jours (Plan Mensuel) à ce restaurant ?')) {
                          await grantFreeSubscription(selectedRestaurant.id, 'mensuel', 30);
                          alert('Abonnement mensuel offert avec succès !');
                        }
                      }}
                      className="bg-white text-red-600 border border-red-200 py-3 rounded-2xl text-[10px] font-black uppercase shadow-sm hover:bg-red-50 transition-colors flex flex-col items-center gap-1"
                    >
                      <span>{selectedRestaurant.subscriptionType === 'mensuel' || selectedRestaurant.subscriptionType === 'annuel' ? 'Prolonger 1 Mois' : 'Offrir 1 Mois'}</span>
                      <span className="text-[8px] opacity-70">(Mensuel) 🎁</span>
                    </button>
                  )}
                  
                  {(selectedRestaurant.subscriptionType === 'commission' || !selectedRestaurant.subscriptionType || selectedRestaurant.subscriptionType === 'none' || selectedRestaurant.subscriptionType === 'trial') && (
                    <button 
                      onClick={async () => {
                        if (confirm('Prolonger la validité Commission de 30 jours pour ce restaurant ?')) {
                          await grantFreeSubscription(selectedRestaurant.id, 'commission', 30);
                          alert('Validité commission prolongée avec succès !');
                        }
                      }}
                      className="bg-white text-red-600 border border-red-200 py-3 rounded-2xl text-[10px] font-black uppercase shadow-sm hover:bg-red-50 transition-colors flex flex-col items-center gap-1"
                    >
                      <span>{selectedRestaurant.subscriptionType === 'commission' ? 'Prolonger 30 j' : 'Activer Commission'}</span>
                      <span className="text-[8px] opacity-70">(Commission) ⏳</span>
                    </button>
                  )}
               </div>
               <p className="text-[9px] text-red-400 mt-3 font-medium italic text-center">
                 * Type actuel : <span className="font-bold uppercase">{selectedRestaurant.subscriptionType || 'Aucun'}</span>
               </p>
            </div>


            {/* Payment History Section */}
            <div className="border-t border-gray-100 pt-8 mt-4">
              <h3 className="text-xl font-black text-gray-900 mb-6">Historique des paiements</h3>
              <div className="space-y-4">
                {allPayments
                  .filter(p => (p.restaurantId || '').trim() === selectedRestaurant.id.trim())
                  .map((payment) => (
                    <div 
                      key={payment.id} 
                      className="bg-gray-50 p-5 rounded-[2rem] border border-gray-100 flex flex-col gap-3"
                    >
                      {/* Top row: proof thumbnail + plan info + status badge */}
                      <div className="flex gap-4 items-start">
                        {(() => {
                          const proof = payment.proofUrl || payment.proofImage || (payment as any).proof_url || (payment as any).proof;
                          if (proof === 'auto_commission') {
                            return (
                              <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center border border-gray-100 shadow-sm shrink-0">
                                <ClipboardListIcon className="w-8 h-8 text-gray-400" />
                              </div>
                            );
                          }
                          if (proof) {
                            return (
                              <div 
                                className="relative group cursor-pointer w-16 h-16 shrink-0"
                                onClick={() => setViewingPaymentImage(proof)}
                              >
                                 <img 
                                   src={proof} 
                                   alt="Preuve" 
                                   className="w-16 h-16 rounded-2xl object-cover ring-2 ring-white shadow-sm group-hover:scale-105 transition-transform" 
                                 />
                                 <div className="absolute inset-0 bg-black/40 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                   <ShieldCheckIcon className="w-6 h-6 text-white" />
                                 </div>
                              </div>
                            );
                          }
                          return <div className="w-16 h-16 rounded-2xl bg-gray-200 flex items-center justify-center shrink-0"><span className="text-[8px] text-gray-400 font-bold uppercase text-center">Aucune preuve</span></div>;
                        })()}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="font-black text-gray-900 text-[11px] uppercase tracking-tighter">Plan {payment.planType}</p>
                            <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider ${
                              payment.status === 'validated' ? 'bg-green-100 text-green-700' : 
                              payment.status === 'rejected' ? 'bg-red-100 text-red-700' : 
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {payment.status === 'validated' ? 'Payé' : payment.status === 'rejected' ? 'Rejeté' : 'En attente'}
                            </span>
                          </div>
                          <p className="text-[9px] font-bold text-gray-400">
                             {formatDateReadable(payment.createdAt)}
                          </p>
                          <p className="text-[8px] font-medium text-gray-300 font-mono mt-0.5">ID: {payment.id.substring(0, 8)}...</p>
                          
                          {((payment as any).expiryDate || (payment as any).validUntil) && (
                             <p className="text-[9px] font-black text-green-600 uppercase mt-0.5">
                               Valid. jusqu'au: {formatDateReadable((payment as any).expiryDate || (payment as any).validUntil)}
                             </p>
                           )}
                          <p className="text-xs font-black text-red-600 mt-1">
                            {payment.amount} DA • {payment.period || (payment.planType === 'annuel' ? '1 an' : '30 jours')}
                          </p>
                          {payment.status === 'validated' && (payment as any).actualPaidAmount !== undefined && (
                             <div className="mt-1 flex items-center gap-2">
                                <p className="text-[10px] font-black text-green-600 bg-green-50 px-2 py-0.5 rounded-lg border border-green-100">
                                  Payé: {(payment as any).actualPaidAmount} DA 
                                </p>
                                {Number((payment as any).actualPaidAmount) < Number(payment.amount) && (
                                  <p className="text-[10px] font-black text-red-600 bg-red-50 px-2 py-0.5 rounded-lg border border-red-100">
                                    Dette générée: {Number(payment.amount) - Number((payment as any).actualPaidAmount)} DA
                                  </p>
                                )}
                             </div>
                           )}
                          {payment.description && (
                            <p className="text-[10px] font-bold text-gray-500 mt-1 bg-white/50 p-2 rounded-lg border border-gray-100 italic">
                                "{payment.description}"
                            </p>
                          )}
                        </div>
                      </div>

                        <div className="flex gap-2">
                          <button 
                            onClick={async () => { 
                              const amountStr = prompt(`Montant réellement reçu ? (Attendu: ${payment.amount} DA)`, payment.amount.toString());
                              if (amountStr !== null) {
                                const finalAmount = Number(amountStr);
                                if (!isNaN(finalAmount)) {
                                  await validatePayment(payment.id, selectedRestaurant.id, finalAmount);
                                  alert(`Paiement de ${finalAmount} DA validé.`);
                                } else {
                                  alert("Montant invalide.");
                                }
                              }
                            }}
                            className="flex-1 bg-green-500 text-white px-4 py-2.5 rounded-2xl text-[10px] font-black uppercase shadow-lg shadow-green-100 active:scale-95 transition-all"
                          >
                            Valider
                          </button>
                          <button 
                            onClick={() => { if(confirm('Rejeter?')) rejectPayment(payment.id) }}
                            className="flex-1 bg-red-50 text-red-600 px-4 py-2.5 rounded-2xl text-[10px] font-black uppercase border border-red-100 active:scale-95 transition-all"
                          >
                            Rejeter
                          </button>
                        </div>
                    </div>
                  ))}
                {allPayments.filter(p => (p.restaurantId || '').trim() === selectedRestaurant.id.trim()).length === 0 && (
                  <div className="text-center py-12 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                    <ClockIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400 font-medium italic">Aucun historique de paiement pour ce restaurant</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Overlay */}
      {viewingPaymentImage && (
        <div className="fixed inset-0 z-[200] bg-black/95 flex flex-col items-center justify-center p-4">
           <button 
             onClick={() => setViewingPaymentImage(null)}
             className="absolute top-8 right-8 bg-white/10 p-3 rounded-full text-white backdrop-blur-md"
           >
             <XIcon className="w-8 h-8" />
           </button>
           <img 
             src={viewingPaymentImage} 
             alt="Preuve Plein Ecran" 
             className="max-w-full max-h-[85vh] object-contain rounded-3xl shadow-[0_0_100px_rgba(255,255,255,0.1)]" 
           />
           <p className="text-white font-black mt-8 text-xl tracking-widest uppercase italic">Preuve de Paiement</p>
        </div>
      )}
    </div>
  );
};

export default SuperAdminRestaurants;
